/**
 * Stroke paths for letter tracing.
 *
 * Each letter is described as ordered strokes, each stroke as ordered points in
 * the unit letter box (origin top left, y downward). The order IS the teaching:
 * strokes run in formation order and points run in the direction the pencil
 * should travel, so the renderer can enforce "start at the top" rather than
 * accepting a letter drawn upside down.
 *
 * Lowercase letters are introduced in formation order (c, o, s, v, w, x, z →
 * a, d, g, q → b, h, p, y) because that is the order that transfers.
 */

import type { NormalizedPoint } from './common'

export const STROKE_DIRECTIONS = [
  'top_to_bottom',
  'bottom_to_top',
  'left_to_right',
  'right_to_left',
  'clockwise',
  'counterclockwise',
  'freeform',
] as const

export type StrokeDirection = (typeof STROKE_DIRECTIONS)[number]

export interface StrokeTemplate {
  id: string
  points: NormalizedPoint[]
  direction: StrokeDirection
  startHint: string
}

export interface LetterTemplate {
  char: string
  letterCase: 'upper' | 'lower'
  strokes: StrokeTemplate[]
}

const p = (x: number, y: number): NormalizedPoint => ({ x, y })

const UPPER: Record<string, StrokeTemplate[]> = {
  A: [
    { id: 'a1', points: [p(0.5, 0.06), p(0.15, 0.94)], direction: 'top_to_bottom', startHint: 'start at the top point, slide down to the left' },
    { id: 'a2', points: [p(0.5, 0.06), p(0.85, 0.94)], direction: 'top_to_bottom', startHint: 'back to the top point, slide down to the right' },
    { id: 'a3', points: [p(0.26, 0.64), p(0.74, 0.64)], direction: 'left_to_right', startHint: 'across the middle' },
  ],
  C: [
    {
      id: 'c1',
      points: [p(0.8, 0.2), p(0.5, 0.05), p(0.2, 0.3), p(0.2, 0.7), p(0.5, 0.95), p(0.8, 0.8)],
      direction: 'counterclockwise',
      startHint: 'start near the top, curve back and around',
    },
  ],
  E: [
    { id: 'e1', points: [p(0.25, 0.06), p(0.25, 0.94)], direction: 'top_to_bottom', startHint: 'down the tall line' },
    { id: 'e2', points: [p(0.25, 0.06), p(0.8, 0.06)], direction: 'left_to_right', startHint: 'across the top' },
    { id: 'e3', points: [p(0.25, 0.5), p(0.72, 0.5)], direction: 'left_to_right', startHint: 'across the middle' },
    { id: 'e4', points: [p(0.25, 0.94), p(0.8, 0.94)], direction: 'left_to_right', startHint: 'across the bottom' },
  ],
  H: [
    { id: 'h1', points: [p(0.2, 0.06), p(0.2, 0.94)], direction: 'top_to_bottom', startHint: 'down the left line' },
    { id: 'h2', points: [p(0.8, 0.06), p(0.8, 0.94)], direction: 'top_to_bottom', startHint: 'down the right line' },
    { id: 'h3', points: [p(0.2, 0.5), p(0.8, 0.5)], direction: 'left_to_right', startHint: 'join them in the middle' },
  ],
  I: [
    { id: 'i1', points: [p(0.26, 0.08), p(0.74, 0.08)], direction: 'left_to_right', startHint: 'a hat across the top' },
    { id: 'i2', points: [p(0.5, 0.08), p(0.5, 0.92)], direction: 'top_to_bottom', startHint: 'down the middle' },
    { id: 'i3', points: [p(0.26, 0.92), p(0.74, 0.92)], direction: 'left_to_right', startHint: 'shoes across the bottom' },
  ],
  L: [
    { id: 'l1', points: [p(0.25, 0.06), p(0.25, 0.92)], direction: 'top_to_bottom', startHint: 'straight down' },
    { id: 'l2', points: [p(0.25, 0.92), p(0.8, 0.92)], direction: 'left_to_right', startHint: 'then a foot to the right' },
  ],
  O: [
    {
      id: 'o1',
      points: [p(0.5, 0.05), p(0.2, 0.3), p(0.2, 0.7), p(0.5, 0.95), p(0.8, 0.7), p(0.8, 0.3), p(0.5, 0.05)],
      direction: 'counterclockwise',
      startHint: 'start at the top and go all the way round',
    },
  ],
  R: [
    { id: 'r1', points: [p(0.2, 0.05), p(0.2, 0.95)], direction: 'top_to_bottom', startHint: 'down the tall line' },
    {
      id: 'r2',
      points: [p(0.2, 0.05), p(0.6, 0.05), p(0.78, 0.2), p(0.6, 0.45), p(0.2, 0.45)],
      direction: 'clockwise',
      startHint: 'back to the top, round the bump',
    },
    { id: 'r3', points: [p(0.45, 0.45), p(0.82, 0.95)], direction: 'top_to_bottom', startHint: 'kick the leg out' },
  ],
  S: [
    {
      id: 's1',
      points: [p(0.78, 0.18), p(0.5, 0.05), p(0.24, 0.2), p(0.36, 0.45), p(0.66, 0.56), p(0.76, 0.78), p(0.5, 0.95), p(0.22, 0.82)],
      direction: 'freeform',
      startHint: 'curve back, then swing forward',
    },
  ],
  T: [
    { id: 't1', points: [p(0.2, 0.08), p(0.8, 0.08)], direction: 'left_to_right', startHint: 'across the top' },
    { id: 't2', points: [p(0.5, 0.08), p(0.5, 0.94)], direction: 'top_to_bottom', startHint: 'down the middle' },
  ],
  U: [
    {
      id: 'u1',
      points: [p(0.2, 0.05), p(0.2, 0.7), p(0.35, 0.9), p(0.65, 0.9), p(0.8, 0.7), p(0.8, 0.05)],
      direction: 'counterclockwise',
      startHint: 'down, around the bottom, back up',
    },
  ],
  X: [
    { id: 'x1', points: [p(0.2, 0.06), p(0.8, 0.94)], direction: 'top_to_bottom', startHint: 'down to the right' },
    { id: 'x2', points: [p(0.8, 0.06), p(0.2, 0.94)], direction: 'top_to_bottom', startHint: 'cross it, down to the left' },
  ],
}

const LOWER: Record<string, StrokeTemplate[]> = {
  a: [
    {
      id: 'a1',
      points: [p(0.5, 0.32), p(0.25, 0.5), p(0.25, 0.78), p(0.5, 0.95), p(0.75, 0.78), p(0.75, 0.5), p(0.5, 0.32)],
      direction: 'counterclockwise',
      startHint: 'round like a c, all the way back',
    },
    { id: 'a2', points: [p(0.75, 0.32), p(0.75, 0.95)], direction: 'top_to_bottom', startHint: 'then straight down the side' },
  ],
  c: [
    {
      id: 'c1',
      points: [p(0.78, 0.45), p(0.5, 0.32), p(0.25, 0.55), p(0.25, 0.78), p(0.5, 0.95), p(0.78, 0.86)],
      direction: 'counterclockwise',
      startHint: 'start under the top, curve back and around',
    },
  ],
  d: [
    {
      id: 'd1',
      points: [p(0.5, 0.32), p(0.25, 0.5), p(0.25, 0.78), p(0.5, 0.95), p(0.75, 0.78), p(0.75, 0.5), p(0.5, 0.32)],
      direction: 'counterclockwise',
      startHint: 'round like a c first',
    },
    { id: 'd2', points: [p(0.78, 0.05), p(0.78, 0.95)], direction: 'top_to_bottom', startHint: 'then a tall line down the side' },
  ],
  o: [
    {
      id: 'o1',
      points: [p(0.5, 0.32), p(0.25, 0.5), p(0.25, 0.78), p(0.5, 0.95), p(0.75, 0.78), p(0.75, 0.5), p(0.5, 0.32)],
      direction: 'counterclockwise',
      startHint: 'start at the top, go round and close it',
    },
  ],
  s: [
    {
      id: 's1',
      points: [p(0.74, 0.42), p(0.5, 0.32), p(0.28, 0.44), p(0.4, 0.62), p(0.64, 0.72), p(0.7, 0.86), p(0.48, 0.95), p(0.26, 0.86)],
      direction: 'freeform',
      startHint: 'curve back, then swing forward',
    },
  ],
  v: [
    {
      id: 'v1',
      points: [p(0.25, 0.32), p(0.5, 0.95), p(0.75, 0.32)],
      direction: 'freeform',
      startHint: 'down to the point, then back up',
    },
  ],
  w: [
    {
      id: 'w1',
      points: [p(0.14, 0.32), p(0.33, 0.95), p(0.5, 0.5), p(0.67, 0.95), p(0.86, 0.32)],
      direction: 'freeform',
      startHint: 'down, up, down, up',
    },
  ],
  x: [
    { id: 'x1', points: [p(0.25, 0.32), p(0.75, 0.95)], direction: 'top_to_bottom', startHint: 'down to the right' },
    { id: 'x2', points: [p(0.75, 0.32), p(0.25, 0.95)], direction: 'top_to_bottom', startHint: 'cross it, down to the left' },
  ],
  z: [
    {
      id: 'z1',
      points: [p(0.24, 0.36), p(0.76, 0.36), p(0.24, 0.94), p(0.76, 0.94)],
      direction: 'freeform',
      startHint: 'across, down the slide, across again',
    },
  ],
}

/** Introduction order for lowercase — easiest shapes first. */
export const LOWERCASE_FORMATION_ORDER: readonly string[] = ['c', 'o', 's', 'v', 'w', 'x', 'z', 'a', 'd']

/** Uppercase she already loves, starting with the letters in her own name. */
export const UPPERCASE_ORDER: readonly string[] = ['A', 'U', 'R', 'O', 'T', 'I', 'L', 'H', 'E', 'C', 'S', 'X']

export function strokeTemplate(char: string): LetterTemplate | undefined {
  const upper = UPPER[char]
  if (upper) return { char, letterCase: 'upper', strokes: upper.map(cloneStroke) }
  const lower = LOWER[char]
  if (lower) return { char, letterCase: 'lower', strokes: lower.map(cloneStroke) }
  return undefined
}

export function hasStrokeTemplate(char: string): boolean {
  return char in UPPER || char in LOWER
}

function cloneStroke(stroke: StrokeTemplate): StrokeTemplate {
  return { ...stroke, points: stroke.points.map((pt) => ({ ...pt })) }
}
